import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "timeago.js";
import { useNotifications } from "../hooks/useNotifications";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  NOTIFICATION_TAB_KEYS,
  NOTIFICATION_TYPES_BY_TAB,
  isServiceEventNotification,
} from "../utils/notificationTypes";
import { navigateWithMobileDashboardScroll } from "../utils/navigateWithMobileDashboardScroll";
import { resolveNotificationNavigationPath } from "../utils/notificationNavigation";

const AllNotifications = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(isAdmin);
  const [categoryTab, setCategoryTab] = useState(NOTIFICATION_TAB_KEYS.ALL);
  const [filter, setFilter] = useState("all"); // "all", "unread", "read"

  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/admin-");

  const orderTypes = NOTIFICATION_TYPES_BY_TAB[NOTIFICATION_TAB_KEYS.ORDERS] || [];
  const enquiryTypes = NOTIFICATION_TYPES_BY_TAB[NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES] || [];
  const vaMediationTypes = NOTIFICATION_TYPES_BY_TAB[NOTIFICATION_TAB_KEYS.VA_MEDIATION] || [];
  const eventTypes = NOTIFICATION_TYPES_BY_TAB[NOTIFICATION_TAB_KEYS.EVENTS] || [];
  const veteranTypes = NOTIFICATION_TYPES_BY_TAB[NOTIFICATION_TAB_KEYS.VETERAN] || [];
  const otherTypes = NOTIFICATION_TYPES_BY_TAB[NOTIFICATION_TAB_KEYS.OTHERS] || [];

  const filteredByCategory = notifications.filter((notif) => {
    if (categoryTab === NOTIFICATION_TAB_KEYS.ALL) return true;
    if (categoryTab === NOTIFICATION_TAB_KEYS.ORDERS) return orderTypes.includes(notif.type);
    if (categoryTab === NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES) return enquiryTypes.includes(notif.type);
    if (categoryTab === NOTIFICATION_TAB_KEYS.VA_MEDIATION) return vaMediationTypes.includes(notif.type);
    if (categoryTab === NOTIFICATION_TAB_KEYS.EVENTS)
      return eventTypes.includes(notif.type) && !isServiceEventNotification(notif);
    if (categoryTab === NOTIFICATION_TAB_KEYS.SERVICES) return isServiceEventNotification(notif);
    if (categoryTab === NOTIFICATION_TAB_KEYS.VETERAN) return veteranTypes.includes(notif.type);
    if (categoryTab === NOTIFICATION_TAB_KEYS.OTHERS) return otherTypes.includes(notif.type);
    return true;
  });

  const filteredNotifications = filteredByCategory.filter((notif) => {
    if (filter === "unread") return !notif.read;
    if (filter === "read") return notif.read;
    return true;
  });

  const tabUnreadCounts = {
    [NOTIFICATION_TAB_KEYS.ALL]: notifications.filter((n) => !n.read).length,
    [NOTIFICATION_TAB_KEYS.ORDERS]: notifications.filter((n) => !n.read && orderTypes.includes(n.type)).length,
    [NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES]: notifications.filter((n) => !n.read && enquiryTypes.includes(n.type)).length,
    [NOTIFICATION_TAB_KEYS.VA_MEDIATION]: notifications.filter((n) => !n.read && vaMediationTypes.includes(n.type)).length,
    [NOTIFICATION_TAB_KEYS.EVENTS]: notifications.filter(
      (n) => !n.read && eventTypes.includes(n.type) && !isServiceEventNotification(n)
    ).length,
    [NOTIFICATION_TAB_KEYS.SERVICES]: notifications.filter((n) => !n.read && isServiceEventNotification(n)).length,
    [NOTIFICATION_TAB_KEYS.VETERAN]: notifications.filter((n) => !n.read && veteranTypes.includes(n.type)).length,
    [NOTIFICATION_TAB_KEYS.OTHERS]: notifications.filter((n) => !n.read && otherTypes.includes(n.type)).length,
  };

  const handleNotificationClick = async (notification) => {
    // For message notifications, don't mark as read here - let inbox page mark when conversation opens
    // For other notifications (orders, offers, etc.), mark as read when clicked
    if (!notification.read && notification.type !== "message_received") {
      await markAsRead(notification._id);
    }
    const type = String(notification?.type || "");
    const relatedId = notification?.relatedId?.toString?.() || notification?.relatedId;
    const isRefundFlow =
      type === "order_refund" ||
      type.includes("refund") ||
      type.includes("return");
    const isVaMediation =
      type === "dispute_escalation" || type === "order_dispute_outcome";
    if (isAdminRoute && isVaMediation && relatedId) {
      navigateWithMobileDashboardScroll(navigate, `/admin/va-mediation?order=${relatedId}`);
      return;
    }
    if (isAdminRoute && isRefundFlow && relatedId) {
      navigateWithMobileDashboardScroll(navigate, `/admin-order/${relatedId}`);
      return;
    }
    const path = resolveNotificationNavigationPath(notification, { isAdminRoute });
    if (path) {
      navigateWithMobileDashboardScroll(navigate, path);
    }
  };

  const getNotificationIcon = (type, notification) => {
    if (notification && isServiceEventNotification(notification)) {
      return "🛎️";
    }
    switch (type) {
      case "order_placed":
      case "order_confirmed":
      case "order_shipped":
      case "order_delivered":
      case "admin_order":
      case "admin_refund":
      case "dispute_escalation":
      case "order_dispute_outcome":
        return "⚖️";
      case "order_refund":
      case "refund_approved":
      case "refund_rejected":
      case "refund_resolved":
      case "return_required":
      case "return_in_transit":
      case "return_received":
        return "📦";
      case "offer_received":
      case "offer_accepted":
      case "offer_rejected":
      case "offer_countered":
        return "💰";
      case "message_received":
        return "💬";
      case "event_request":
      case "event_approved":
      case "event_rejected":
      case "event_amended":
        return "📅";
      default:
        return "🔔";
    }
  };

  const getNotificationColor = (type) => {
    if (type === "dispute_escalation" || type === "order_dispute_outcome")
      return "bg-amber-100 text-amber-800";
    if (type.includes("order") || type.includes("refund") || type.includes("return"))
      return "bg-blue-100 text-blue-800";
    if (type.includes("offer")) return "bg-green-100 text-green-800";
    if (type.includes("message")) return "bg-purple-100 text-purple-800";
    return "bg-gray-100 text-gray-800";
  };

  const getNotificationTypeLabel = (notification) => {
    const rawType = String(notification?.type || "");
    const defaultLabel = rawType.replace(/_/g, " ");
    const explicitLabels = {
      order_refund: "refund requested",
      refund_approved: "refund approved",
      refund_rejected: "refund rejected",
      refund_resolved: "refund resolved",
      admin_order: "new order placed",
      admin_refund: "refund request",
      dispute_escalation: "VA mediation request",
      order_dispute_outcome: "VA mediation update",
      return_required: "return required",
      return_in_transit: "return shipped",
      return_received: "return received",
    };

    if (String(notification?.title || "").toLowerCase().includes("va mediation reply")) {
      return "VA mediation reply";
    }

    if (explicitLabels[rawType]) return explicitLabels[rawType];

    // Backward compatibility: service notifications still use event_* type codes.
    // Show user-facing "service ..." labels when content/link indicates service context.
    const looksLikeService =
      /service/i.test(notification?.title || "") ||
      /service/i.test(notification?.message || "") ||
      /service/i.test(notification?.link || "");

    if (looksLikeService && rawType.startsWith("event_")) {
      return defaultLabel.replace(/^event/i, "service");
    }

    return defaultLabel;
  };

  const cardClass = "rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8";

  return (
    <section className={cardClass}>
      {/* Header */}
      <div className="mb-6 pb-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <IoMdNotificationsOutline size={28} className="text-[#38513b]" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="px-4 py-2 text-sm font-medium text-[#38513b] hover:bg-[#38513b]/10 rounded-xl transition"
                >
                  Mark all as read
                </button>
              )}
            </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { key: NOTIFICATION_TAB_KEYS.ALL, label: "All" },
            { key: NOTIFICATION_TAB_KEYS.ORDERS, label: "Orders" },
            { key: NOTIFICATION_TAB_KEYS.VA_MEDIATION, label: "VA Mediation" },
            { key: NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES, label: "Customer Enquiries" },
            { key: NOTIFICATION_TAB_KEYS.EVENTS, label: "Events" },
            { key: NOTIFICATION_TAB_KEYS.SERVICES, label: "Services" },
            { key: NOTIFICATION_TAB_KEYS.VETERAN, label: "Veteran" },
            { key: NOTIFICATION_TAB_KEYS.OTHERS, label: "Others" },
          ].map(({ key, label }) => {
            const count = tabUnreadCounts[key] || 0;
            const active = categoryTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setCategoryTab(key)}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition flex items-center gap-1.5 ${
                  active ? "bg-[#38513b] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {label}
                {count > 0 && (
                  <span className={`min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-xs font-semibold ${
                    active ? "bg-white/20" : "bg-[#38513b]/15 text-[#38513b]"
                  }`}>
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* Read state filters */}
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              filter === "all" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilter("unread")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              filter === "unread" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Unread
          </button>
          <button
            type="button"
            onClick={() => setFilter("read")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              filter === "read" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Read
          </button>
        </div>
      </div>

      {/* Notifications List */}
        <div className="divide-y divide-slate-100">
          {filteredNotifications.length === 0 ? (
            <div className="py-12 text-center">
              <IoMdNotificationsOutline size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg font-medium">
                {categoryTab !== NOTIFICATION_TAB_KEYS.ALL
                  ? `No ${filter === "unread" ? "unread " : filter === "read" ? "read " : ""}notifications in ${
                      categoryTab === NOTIFICATION_TAB_KEYS.ORDERS ? "Orders" :
                      categoryTab === NOTIFICATION_TAB_KEYS.VA_MEDIATION ? "VA Mediation" :
                      categoryTab === NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES ? "Customer Enquiries" :
                      categoryTab === NOTIFICATION_TAB_KEYS.EVENTS ? "Events" :
                      categoryTab === NOTIFICATION_TAB_KEYS.SERVICES ? "Services" :
                      categoryTab === NOTIFICATION_TAB_KEYS.VETERAN ? "Veteran" : "Others"
                    }`
                  : filter === "unread"
                  ? "No unread notifications"
                  : filter === "read"
                  ? "No read notifications"
                  : "No notifications yet"}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <button
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                  !notification.read ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">
                    {getNotificationIcon(notification.type, notification)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p
                            className={`text-base font-semibold ${
                              !notification.read ? "text-slate-900" : "text-slate-700"
                            }`}
                          >
                            {notification.title}
                          </p>
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${getNotificationColor(
                              notification.type
                            )}`}
                          >
                            {getNotificationTypeLabel(notification)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-2">
                          {format(notification.createdAt)}
                        </p>
                      </div>
                      {!notification.read && (
                        <span className="h-3 w-3 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
    </section>
  );
};

export default AllNotifications;
